import { useMemo } from "react";
import type { SurfQueuedJob, SurfSlide } from "@vida/surf-core";
import { useSurfJobQueue } from "../../hooks/useSurfJobQueue";
import { useSurfAssetThumbs } from "../../hooks/useSurfAssetThumbs";

const OP_LABEL: Record<string, string> = {
  "slide.background": "Sfondo",
  "slide.raster.text": "Illustrazione",
  "layer.image": "Immagine widget",
  "image.edit": "Modifica",
  outline: "Outline",
  "style.preview": "Anteprima stile",
  "speaker.script": "Discorso speaker",
};

type Props = {
  deckId: string;
  slide: SurfSlide;
};

/** Version gallery: every AI-generated raster asset for the slide and its widgets. */
export function SlideHistoryPanel({ deckId, slide }: Props) {
  const { deckJobs, restoreVersion } = useSurfJobQueue();

  const groups = useMemo(() => {
    const jobs = deckJobs(deckId).filter(
      (j) => j.target.slideId === slide.id && typeof j.outputAssetKey === "string",
    );
    const byTarget = new Map<string, { name: string; jobs: SurfQueuedJob[] }>();
    for (const j of jobs) {
      const key = j.target.layerId ?? "__slide__";
      const layer = j.target.layerId
        ? slide.layers.find((l) => l.id === j.target.layerId)
        : undefined;
      const name = j.target.layerId
        ? layer
          ? `Widget · ${layer.type}${layer.textRole ? ` · ${layer.textRole}` : ""}`
          : "Widget rimosso"
        : "Sfondo slide";
      const g = byTarget.get(key) ?? { name, jobs: [] };
      g.jobs.push(j);
      byTarget.set(key, g);
    }
    return [...byTarget.values()].map((g) => ({
      ...g,
      jobs: g.jobs.sort((a, b) => Date.parse(b.submittedAt) - Date.parse(a.submittedAt)),
    }));
  }, [deckJobs, deckId, slide]);

  const allKeys = useMemo(
    () => groups.flatMap((g) => g.jobs.map((j) => j.outputAssetKey!).filter(Boolean)),
    [groups],
  );
  const thumbs = useSurfAssetThumbs(allKeys);

  if (groups.length === 0) {
    return <p className="muted">Nessuna risorsa immagine generata per questa slide.</p>;
  }

  return (
    <section className="slide-history">
      {groups.map((g, gi) => (
        <div className="slide-history-group" key={gi}>
          <h4>{g.name}</h4>
          <div className="slide-history-versions">
            {g.jobs.map((j, i) => {
              const url = thumbs[j.outputAssetKey!];
              const isCurrent = i === 0;
              return (
                <figure className="slide-history-version" key={j.jobId}>
                  <div className="slide-history-thumb">
                    {url ? (
                      <img src={url} alt={`${OP_LABEL[j.op] ?? j.op} ${i + 1}`} loading="lazy" />
                    ) : (
                      <span className="muted">…</span>
                    )}
                  </div>
                  <figcaption>
                    <span className="muted">
                      {OP_LABEL[j.op] ?? j.op} · {new Date(j.submittedAt).toLocaleString()}
                    </span>
                    {isCurrent ? (
                      <span className="slide-history-current">attuale</span>
                    ) : (
                      <button
                        type="button"
                        className="surf-btn surf-btn--ghost"
                        onClick={() => void restoreVersion(j)}
                      >
                        Ripristina
                      </button>
                    )}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
